import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  X,
  Calendar,
  User,
  ShoppingBag,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Clock,
  ChevronDown,
  Sparkles,
  Phone,
  Check,
  AlertCircle,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useLenis } from "lenis/react";

const BookingModal = () => {
  const lenis = useLenis();
  const shouldReduceMotion = useReducedMotion();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Datos de API
  const [services, setServices] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [combos, setCombos] = useState([]);
  const [staffList, setStaffList] = useState([]);

  // Selecciones del usuario
  const [category, setCategory] = useState("ella"); // 'ella' | 'el' | 'duo'
  const [activeTab, setActiveTab] = useState("servicios"); // 'servicios' | 'combos'
  const [openTipoId, setOpenTipoId] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Disponibilidad de Slots
  const [selectedDate, setSelectedDate] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [formData, setFormData] = useState({
    nombre_cliente: "",
    email_cliente: "",
    telefono_cliente: "",
    fecha_nacimiento: "",
    notas: "",
  });

  // Estado de verificación telefónica e identidad
  const [phoneCheckStatus, setPhoneCheckStatus] = useState("idle"); // 'idle' | 'checking' | 'exists_active' | 'exists_inactive' | 'not_found' | 'error'
  const [phoneCheckResult, setPhoneCheckResult] = useState(null);
  const [isConfirmedSelf, setIsConfirmedSelf] = useState(false);
  const [isRejectedSelf, setIsRejectedSelf] = useState(false);
  const [phoneErrorMessage, setPhoneErrorMessage] = useState(null);

  const [pendingServiceName, setPendingServiceName] = useState(null);

  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const lastActiveElementRef = useRef(null);
  const tabServiciosRef = useRef(null);
  const tabCombosRef = useRef(null);

  // Escuchar evento para abrir modal
  useEffect(() => {
    const handleOpen = (e) => {
      const cat = e.detail?.category || "ella";
      const sName = e.detail?.serviceName || null;
      if (typeof document !== "undefined") {
        lastActiveElementRef.current = document.activeElement;
      }
      setCategory(cat);
      setActiveTab("servicios");
      setOpenTipoId(null);
      setPendingServiceName(sName);
      setStep(1);
      setSelectedServices([]);
      setSelectedStaff(null);
      setSelectedDate("");
      setAvailableSlots([]);
      setSelectedTime("");
      setSuccess(null);
      setError(null);
      setFormData({
        nombre_cliente: "",
        email_cliente: "",
        telefono_cliente: "",
        fecha_nacimiento: "",
        notas: "",
      });
      setPhoneCheckStatus("idle");
      setPhoneCheckResult(null);
      setIsConfirmedSelf(false);
      setIsRejectedSelf(false);
      setPhoneErrorMessage(null);
      setIsOpen(true);
    };

    window.addEventListener("open-booking", handleOpen);
    return () => window.removeEventListener("open-booking", handleOpen);
  }, []);

  // Bloquear scroll de la landing y pausar Lenis mientras el modal de reserva esté abierto
  useEffect(() => {
    if (!isOpen) return;

    const lenisInstance = lenis || (typeof window !== "undefined" ? window.__lenis : null);
    let wasStopped = false;

    if (lenisInstance) {
      wasStopped = !!lenisInstance.isStopped;
      if (!wasStopped && typeof lenisInstance.stop === "function") {
        lenisInstance.stop();
      }
    }

    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;

      if (lenisInstance && !wasStopped && typeof lenisInstance.start === "function") {
        lenisInstance.start();
      }
    };
  }, [isOpen, lenis]);

  const getFocusableElements = useCallback(() => {
    if (!modalRef.current) return [];
    const elements = modalRef.current.querySelectorAll(
      'button:not([disabled]):not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.from(elements).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0
    );
  }, []);

  // Manejo de tecla Escape, foco inicial y contención de foco (trap focus)
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 50);

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
        return;
      }

      if (e.key === "Tab" && modalRef.current) {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) {
          e.preventDefault();
          return;
        }
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement || !modalRef.current.contains(document.activeElement)) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement || !modalRef.current.contains(document.activeElement)) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", handleKeyDown);
      if (
        lastActiveElementRef.current &&
        typeof lastActiveElementRef.current.focus === "function" &&
        document.contains(lastActiveElementRef.current)
      ) {
        lastActiveElementRef.current.focus();
      }
    };
  }, [isOpen, getFocusableElements]);

  // Asegurar destino de foco válido dentro del modal si el elemento enfocado desaparece o queda huérfano
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;
    const activeEl = document.activeElement;
    if (!activeEl || !modalRef.current.contains(activeEl) || !document.contains(activeEl)) {
      const focusable = getFocusableElements();
      if (focusable.length > 0) {
        focusable[0].focus();
      }
    }
  }, [step, openTipoId, activeTab, isOpen, phoneCheckStatus, isConfirmedSelf, isRejectedSelf, getFocusableElements]);

  // Cargar servicios, tipos y combos al montar
  useEffect(() => {
    fetch("/api/list_services.php")
      .then((res) => {
        if (!res.ok) throw new Error("Fallo al cargar servicios");
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          setServices(data.servicios || []);
          setTipos(data.tipos || []);
          setCombos(data.combos || []);
        } else {
          setServices(Array.isArray(data) ? data : []);
        }
      })
      .catch((err) => console.error("Error cargando servicios:", err));
  }, []);

  // Filtrado derivado por territorio
  const filteredServices = useMemo(() => {
    const catLower = (category || "").toLowerCase();
    return services.filter(
      (s) => s.activo && (s.categoria || "").toLowerCase() === catLower
    );
  }, [category, services]);

  const filteredCombos = useMemo(() => {
    const catLower = (category || "").toLowerCase();
    return combos.filter(
      (c) => !!c.activo && (c.categoria || c.territorio || "").toLowerCase() === catLower
    );
  }, [category, combos]);

  // Agrupación de catálogo por tipos desplegables según pestaña activa
  const catalogGroups = useMemo(() => {
    const isCombosTab = activeTab === "combos";
    const knownTipoIds = new Set(tipos.map((t) => Number(t.id)));

    if (isCombosTab) {
      const groups = tipos
        .map((tipo) => {
          const tipoCombos = filteredCombos.filter(
            (c) => Number(c.tipo_servicio_id) === Number(tipo.id)
          );
          return {
            tipo,
            combos: tipoCombos,
            services: [],
            totalItems: tipoCombos.length,
          };
        })
        .filter((g) => g.totalItems > 0);

      const orphanCombos = filteredCombos.filter(
        (c) => !knownTipoIds.has(Number(c.tipo_servicio_id))
      );
      if (orphanCombos.length > 0) {
        groups.push({
          tipo: { id: "otros_combos", nombre: "Otras Experiencias & Combos", slug: "otros" },
          combos: orphanCombos,
          services: [],
          totalItems: orphanCombos.length,
        });
      }
      return groups;
    } else {
      const groups = tipos
        .map((tipo) => {
          const tipoServices = filteredServices.filter(
            (s) => Number(s.tipo_servicio_id) === Number(tipo.id)
          );
          return {
            tipo,
            combos: [],
            services: tipoServices,
            totalItems: tipoServices.length,
          };
        })
        .filter((g) => g.totalItems > 0);

      const orphanServices = filteredServices.filter(
        (s) => !knownTipoIds.has(Number(s.tipo_servicio_id))
      );
      if (orphanServices.length > 0) {
        groups.push({
          tipo: { id: "otros_servicios", nombre: "Otros Servicios", slug: "otros" },
          combos: [],
          services: orphanServices,
          totalItems: orphanServices.length,
        });
      }
      return groups;
    }
  }, [activeTab, tipos, filteredServices, filteredCombos]);

  // Conteo de selecciones por pestaña para badges
  const selectedServicesCount = useMemo(() => {
    return selectedServices.filter((s) => !s.es_combo).length;
  }, [selectedServices]);

  const selectedCombosCount = useMemo(() => {
    return selectedServices.filter((s) => s.es_combo).length;
  }, [selectedServices]);

  // Manejar preselección de servicio o combo desde evento externo
  useEffect(() => {
    if (pendingServiceName && (filteredServices.length > 0 || filteredCombos.length > 0)) {
      const pLower = pendingServiceName.toLowerCase();
      const foundCombo = filteredCombos.find((c) =>
        (c.nombre || "").toLowerCase().includes(pLower)
      );
      if (foundCombo) {
        setSelectedServices([foundCombo]);
        setActiveTab("combos");
        setOpenTipoId(foundCombo.tipo_servicio_id);
        setStep(2);
      } else {
        const found = filteredServices.find((s) =>
          (s.nombre || "").toLowerCase().includes(pLower)
        );
        if (found) {
          setSelectedServices([found]);
          setActiveTab("servicios");
          setOpenTipoId(found.tipo_servicio_id);
          setStep(2);
        } else {
          setStep(1);
        }
      }
      setPendingServiceName(null);
    }
  }, [pendingServiceName, filteredServices, filteredCombos]);

  // Cambio de territorio con invalidación segura
  const handleCategoryChange = (cat) => {
    if (cat === category) return;
    setCategory(cat);
    setActiveTab("servicios");
    setOpenTipoId(null);
    setSelectedServices([]);
    setSelectedStaff(null);
    setSelectedDate("");
    setSelectedTime("");
    setAvailableSlots([]);
    setError(null);
  };

  // Cargar staff cuando cambian los servicios seleccionados (intersección de profesionales calificados)
  useEffect(() => {
    if (selectedServices.length === 0) {
      setStaffList([]);
      setSelectedStaff(null);
      return;
    }

    setLoading(true);
    let allIdsToFetch = [];
    selectedServices.forEach(s => {
        if (s.es_combo) {
            allIdsToFetch.push(...(s.servicios_ids || []));
        } else {
            allIdsToFetch.push(s.id);
        }
    });

    Promise.all(
      allIdsToFetch.map(sId =>
        fetch(`/api/list_staff.php?service_id=${sId}`).then((res) => {
          if (!res.ok) throw new Error("Fallo al cargar profesionales");
          return res.json();
        })
      )
    )
      .then((lists) => {
        if (lists.length === 0) {
          setStaffList([]);
          setLoading(false);
          return;
        }
        let intersection = lists[0].filter(emp => emp.activo);
        for (let i = 1; i < lists.length; i++) {
          const currentList = lists[i];
          intersection = intersection.filter(emp => 
            currentList.some(e => e.id === emp.id && e.activo)
          );
        }
        setStaffList(intersection);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error cargando profesionales:", err);
        setLoading(false);
      });
  }, [selectedServices]);

  // Cargar disponibilidad de slots cuando cambia la fecha, empleado o servicios
  useEffect(() => {
    if (!selectedDate || selectedServices.length === 0 || !selectedStaff) {
      setAvailableSlots([]);
      setSelectedTime("");
      return;
    }

    setSlotsLoading(true);
    setError(null);
    let serviceIdsArr = [];
    selectedServices.forEach((s) => {
      if (s.es_combo) {
        serviceIdsArr.push(...(s.servicios_ids || []));
      } else {
        serviceIdsArr.push(s.id);
      }
    });
    const serviceIds = serviceIdsArr.join(",");
    fetch(
      `/api/get_available_slots.php?service_ids=${serviceIds}&employee_id=${selectedStaff.id}&date=${selectedDate}`
    )
      .then((res) => {
        if (!res.ok) throw new Error("Error consultando disponibilidad en el servidor");
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          setAvailableSlots(data.slots);
        } else {
          throw new Error(data.error || "Fallo en disponibilidad");
        }
        setSlotsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError(err.message);
        setAvailableSlots([]);
        setSlotsLoading(false);
      });
  }, [selectedDate, selectedServices, selectedStaff]);

  const isItemEqual = (a, b) => a.id === b.id && Boolean(a.es_combo) === Boolean(b.es_combo);

  const isItemSelected = (item) => {
    return selectedServices.some((s) => isItemEqual(s, item));
  };

  const handleToggleItem = (item) => {
    const isSelected = isItemSelected(item);

    if (isSelected) {
      setSelectedServices(selectedServices.filter((s) => !isItemEqual(s, item)));
      setSelectedStaff(null);
      setSelectedDate("");
      setSelectedTime("");
      setAvailableSlots([]);
      return;
    }

    if (item.es_combo) {
      if (selectedServices.length > 0) {
        toast.info(
          "Tienes servicios seleccionados. Para reservar un combo, primero desmarca los servicios.",
          { duration: 4000 }
        );
        return;
      }
      setSelectedServices([item]);
      setSelectedStaff(null);
      setSelectedDate("");
      setSelectedTime("");
      setAvailableSlots([]);
      return;
    }

    const hasCombo = selectedServices.some((s) => s.es_combo);
    if (hasCombo) {
      toast.info(
        "No es posible combinar un combo con otros servicios en la misma reserva. Desmarca el combo primero.",
        { duration: 4000 }
      );
      return;
    }

    setSelectedServices([...selectedServices, item]);
    setSelectedStaff(null);
    setSelectedDate("");
    setSelectedTime("");
    setAvailableSlots([]);
  };

  // Manejador compartido para limpiar selecciones sin alterar territorio ni pestaña
  const handleClearSelections = () => {
    setSelectedServices([]);
    setSelectedStaff(null);
    setSelectedDate("");
    setSelectedTime("");
    setAvailableSlots([]);
    setError(null);
  };

  // Manejador de teclado para pestañas accesibles (WAI-ARIA Tabs pattern)
  const handleTabKeyDown = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      if (activeTab === "servicios") {
        setActiveTab("combos");
        tabCombosRef.current?.focus();
      } else {
        setActiveTab("servicios");
        tabServiciosRef.current?.focus();
      }
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveTab("servicios");
      tabServiciosRef.current?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveTab("combos");
      tabCombosRef.current?.focus();
    }
  };

  const handleStaffSelect = (staff) => {
    setSelectedStaff(staff);
    setStep(3);
  };

  const handleDateTimeSubmit = (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      setError("Por favor selecciona una fecha y una hora disponible");
      return;
    }
    setError(null);
    setStep(4);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Manejador específico de cambio de teléfono con reinicio de estados de verificación
  const handlePhoneChange = (e) => {
    const val = e.target.value;
    setFormData((prev) => ({
      ...prev,
      telefono_cliente: val,
    }));
    setPhoneCheckStatus("idle");
    setPhoneCheckResult(null);
    setIsConfirmedSelf(false);
    setIsRejectedSelf(false);
    setPhoneErrorMessage(null);
  };

  // Consulta al endpoint seguro de verificación de teléfono
  const checkPhone = useCallback(async (phoneNumber) => {
    const clean = (phoneNumber || "").trim();
    const digitsOnly = clean.replace(/[^\d]/g, "");
    if (digitsOnly.length < 7) {
      setPhoneErrorMessage("Ingrese un número telefónico válido (mínimo 7 dígitos o celular de 10 dígitos)");
      return;
    }
    setPhoneErrorMessage(null);
    setPhoneCheckStatus("checking");
    setIsConfirmedSelf(false);
    setIsRejectedSelf(false);

    try {
      const res = await fetch("/api/check_client_phone.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telefono: clean }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setPhoneCheckStatus("error");
        setPhoneErrorMessage(data.error || "No fue posible verificar el número telefónico");
        return;
      }

      if (!data.exists) {
        setPhoneCheckStatus("not_found");
        setPhoneCheckResult({ exists: false });
      } else if (data.activo === false) {
        setPhoneCheckStatus("exists_inactive");
        setPhoneCheckResult(data);
      } else {
        setPhoneCheckStatus("exists_active");
        setPhoneCheckResult(data);
      }
    } catch (err) {
      setPhoneCheckStatus("error");
      setPhoneErrorMessage("Error de conexión al verificar el teléfono. Inténtelo nuevamente.");
    }
  }, []);

  // Debounce para consultar automáticamente el teléfono al digitar un número válido
  useEffect(() => {
    if (step !== 4) return;
    const tel = formData.telefono_cliente.trim();
    const digits = tel.replace(/[^\d]/g, "");
    if (digits.length < 10 && !tel.startsWith("+")) return;

    const timer = setTimeout(() => {
      checkPhone(tel);
    }, 650);

    return () => clearTimeout(timer);
  }, [formData.telefono_cliente, step, checkPhone]);

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const isComboBooking = selectedServices.length === 1 && selectedServices[0].es_combo;
    let comboId = null;
    let servicesPayload = [];

    if (isComboBooking) {
      comboId = selectedServices[0].id;
      servicesPayload = selectedServices[0].servicios_ids.map((id) => ({ servicio_id: id }));
    } else {
      selectedServices.forEach((s) => {
        if (s.es_combo) {
          servicesPayload.push(...s.servicios_ids.map((id) => ({ servicio_id: id })));
        } else {
          servicesPayload.push({
            servicio_id: s.id,
            duracion_minutos: s.duracion_minutos,
            precio_pactado: s.precio,
          });
        }
      });
    }

    const isExistingClient = phoneCheckStatus === "exists_active" && isConfirmedSelf;

    const payload = {
      nombre_cliente: isExistingClient
        ? (formData.nombre_cliente.trim() || phoneCheckResult?.masked_name || "Cliente Registrado")
        : formData.nombre_cliente.trim(),
      email_cliente: isExistingClient
        ? (formData.email_cliente.trim() || "cliente.existente@duoexperience.vip")
        : formData.email_cliente.trim(),
      telefono_cliente: formData.telefono_cliente.trim(),
      servicios: servicesPayload,
      combo_id: comboId,
      empleado_id: selectedStaff.id,
      fecha_hora: `${selectedDate} ${selectedTime}:00`,
      notas: formData.notas,
    };

    if (!isExistingClient && formData.fecha_nacimiento && formData.fecha_nacimiento.trim()) {
      payload.fecha_nacimiento = formData.fecha_nacimiento.trim();
    }

    try {
      const res = await fetch("/api/create_booking.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        if (res.status === 403) {
          setPhoneCheckStatus("exists_inactive");
          setPhoneCheckResult(data);
        }
        throw new Error(data.error || "Fallo en el servidor al agendar cita");
      }

      setSuccess(data);
      setStep(5);
      toast.success("¡Reserva realizada con éxito!");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duracion_minutos, 0);
  const totalValue = selectedServices.reduce((sum, s) => sum + s.precio, 0);

  const getEndTime = (startTimeStr, durationMin) => {
    if (!startTimeStr) return "";
    const [h, m] = startTimeStr.split(":").map(Number);
    const totalMin = h * 60 + m + durationMin;
    const endH = Math.floor(totalMin / 60) % 24;
    const endM = totalMin % 60;
    return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
  };

  if (!isOpen) return null;

  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <AnimatePresence>
      <div
        data-lenis-prevent
        className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overscroll-contain"
        style={{
          paddingTop: "max(0.75rem, env(safe-area-inset-top, 0px))",
          paddingBottom: "max(0.75rem, env(safe-area-inset-bottom, 0px))",
          paddingLeft: "max(0.75rem, env(safe-area-inset-left, 0px))",
          paddingRight: "max(0.75rem, env(safe-area-inset-right, 0px))",
        }}
      >
        {/* Backdrop */}
        <motion.div
          data-lenis-prevent
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
          onClick={() => setIsOpen(false)}
          className="absolute inset-0 bg-black/60 backdrop-blur-md"
          style={{ touchAction: "none" }}
        />

        {/* Modal Card con ancho responsive max-w-4xl y altura segura en dvh */}
        <motion.div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-modal-title"
          data-lenis-prevent
          initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 16 }}
          animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
          exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 16 }}
          transition={{ duration: shouldReduceMotion ? 0 : 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 w-full max-w-4xl overflow-hidden rounded-3xl border border-white/10 bg-[#faf9f6] shadow-2xl flex flex-col h-[92dvh] max-h-[840px] overscroll-contain"
        >
          {/* Header fijo superior */}
          <div className="flex-shrink-0 flex items-center justify-between border-b border-[#3a3939]/10 px-4 sm:px-6 py-3.5 sm:py-4 bg-[#faf9f6]">
            <div>
              <span
                className={`label text-[10px] sm:text-xs font-bold tracking-wider uppercase ${
                  category === "el" ? "text-[#b79b7d]" : "text-[#593d36]"
                }`}
              >
                Reserva tu ritual en línea
              </span>
              <h3 id="booking-modal-title" className="font-serif-display text-lg sm:text-2xl text-[#3a3939]">
                {step === 1 && "1. Elige tus Experiencias"}
                {step === 2 && "2. Elige tu Especialista"}
                {step === 3 && "3. Fecha y Hora"}
                {step === 4 && "4. Tus Datos de Reserva"}
                {step === 5 && "¡Reserva Confirmada!"}
              </h3>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Cerrar modal de reserva"
              className="rounded-full p-2 text-[#6b615c] transition-colors hover:bg-[#3a3939]/5 hover:text-[#3a3939] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Banner de error si existe */}
          {error && (
            <div className="flex-shrink-0 bg-red-50 px-4 sm:px-6 py-2.5 text-xs sm:text-sm text-red-600 border-b border-red-100 font-medium">
              {error}
            </div>
          )}

          {/* Barra de controles del catálogo en Paso 1 (Territorios y Pestañas) */}
          {step === 1 && (
            <div className="flex-shrink-0 border-b border-[#3a3939]/10 bg-[#faf9f6] px-4 sm:px-6 py-3 space-y-3">
              {/* Fila 1: Selector de Territorio */}
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] label text-[#8c8078] uppercase tracking-wider hidden sm:inline">
                  Territorio:
                </span>
                <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0 w-full sm:w-auto">
                  {[
                    { id: "ella", label: "Para Ella" },
                    { id: "el", label: "Para Él" },
                    { id: "duo", label: "Experiencias DUO" },
                  ].map((t) => {
                    const isSelected = category === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleCategoryChange(t.id)}
                        className={`rounded-full px-4 sm:px-5 py-2 label text-xs transition-all whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498] ${
                          isSelected
                            ? t.id === "el"
                              ? "bg-[#b79b7d] text-[#1a1a1a] font-bold shadow-sm"
                              : "bg-[#cea498] text-[#1a1a1a] font-bold shadow-sm"
                            : "bg-[#3a3939]/5 text-[#6b615c] hover:bg-[#3a3939]/10"
                        }`}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Fila 2: Pestañas secundarias (Servicios individuales vs Combos/Experiencias destacada) */}
              <div
                role="tablist"
                aria-label="Catálogo de experiencias"
                className="flex items-center gap-2 pt-1 border-t border-[#3a3939]/5 overflow-x-auto"
              >
                <button
                  ref={tabServiciosRef}
                  type="button"
                  role="tab"
                  id="tab-servicios"
                  aria-selected={activeTab === "servicios"}
                  aria-controls="panel-catalogo"
                  tabIndex={activeTab === "servicios" ? 0 : -1}
                  onClick={() => setActiveTab("servicios")}
                  onKeyDown={handleTabKeyDown}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 sm:px-5 py-2 text-xs transition-all whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498] ${
                    activeTab === "servicios"
                      ? "bg-[#3a3939] text-[#faf9f6] font-semibold shadow-sm"
                      : "bg-[#3a3939]/5 text-[#6b615c] hover:text-[#3a3939] hover:bg-[#3a3939]/10"
                  }`}
                >
                  <span>Servicios individuales</span>
                  <span className="text-[11px] opacity-75">({filteredServices.length})</span>
                  {selectedServicesCount > 0 && (
                    <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-[#cea498] text-[#593d36] font-bold">
                      ✓ {selectedServicesCount}
                    </span>
                  )}
                </button>

                <button
                  ref={tabCombosRef}
                  type="button"
                  role="tab"
                  id="tab-combos"
                  aria-selected={activeTab === "combos"}
                  aria-controls="panel-catalogo"
                  tabIndex={activeTab === "combos" ? 0 : -1}
                  onClick={() => setActiveTab("combos")}
                  onKeyDown={handleTabKeyDown}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 sm:px-5 py-2 text-xs transition-all whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498] ${
                    activeTab === "combos"
                      ? category === "el"
                        ? "bg-gradient-to-r from-[#1a1a1a] to-[#2c2826] text-[#faf9f6] font-bold shadow-md border border-[#b79b7d]"
                        : "bg-gradient-to-r from-[#593d36] to-[#734f46] text-[#faf9f6] font-bold shadow-md border border-[#cea498]/50"
                      : category === "el"
                      ? "border border-[#b79b7d]/40 bg-[#b79b7d]/10 text-[#b79b7d] hover:bg-[#b79b7d]/20 font-medium"
                      : "border border-[#cea498]/40 bg-[#cea498]/10 text-[#593d36] hover:bg-[#cea498]/20 font-medium"
                  }`}
                >
                  <Sparkles className="h-3.5 w-3.5 text-[#cea498]" />
                  <span>Combos & Experiencias</span>
                  <span className="text-[11px] opacity-80">({filteredCombos.length})</span>
                  {selectedCombosCount > 0 && (
                    <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-[#cea498] text-[#1a1a1a] font-bold">
                      ✓ {selectedCombosCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Contenedor central desplazable (Mantiene scroll nativo, overscroll-contain y min-h-0) */}
          <div
            data-lenis-prevent
            className="flex-1 overflow-y-auto min-h-0 p-4 sm:p-6 overscroll-contain"
            style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}
          >
            {/* PASO 1: Selección de Servicio o Combo */}
            {step === 1 && (
              <div
                id="panel-catalogo"
                role="tabpanel"
                aria-labelledby={activeTab === "combos" ? "tab-combos" : "tab-servicios"}
                tabIndex={0}
                className="space-y-4 focus:outline-none"
              >
                {/* Banner informativo si hay un combo seleccionado */}
                {selectedServices.some((s) => s.es_combo) && (
                  <div className="p-3.5 rounded-2xl bg-[#593d36]/10 border border-[#593d36]/20 text-xs text-[#593d36] flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="h-4 w-4 shrink-0 text-[#cea498]" />
                      <span>
                        <strong>Combo seleccionado:</strong> {selectedServices[0].nombre}. Los combos se reservan de
                        forma exclusiva por sesión.
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={handleClearSelections}
                      className="underline font-semibold text-[11px] whitespace-nowrap hover:opacity-80"
                    >
                      Deseleccionar
                    </button>
                  </div>
                )}

                {/* Banner informativo si hay servicios individuales y el usuario explora pestaña Combos */}
                {activeTab === "combos" && selectedServicesCount > 0 && (
                  <div className="p-3.5 rounded-2xl bg-[#cea498]/20 border border-[#cea498]/40 text-xs text-[#593d36] flex items-center justify-between gap-2">
                    <span>
                      Tienes <strong>{selectedServicesCount} {selectedServicesCount === 1 ? "servicio seleccionado" : "servicios seleccionados"}</strong> en la pestaña Servicios. Para reservar un combo, primero desmarca los servicios.
                    </span>
                    <button
                      type="button"
                      onClick={handleClearSelections}
                      className="underline font-semibold text-[11px] whitespace-nowrap hover:opacity-80"
                    >
                      Limpiar
                    </button>
                  </div>
                )}

                {/* Acordeón de Tipos */}
                {catalogGroups.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[#3a3939]/20 p-8 text-center my-6">
                    <p className="font-cormorant text-lg italic text-[#6b615c]">
                      {activeTab === "combos"
                        ? "No hay combos ni experiencias disponibles en esta categoría actualmente."
                        : "No hay servicios individuales disponibles en esta categoría actualmente."}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {catalogGroups.map((group) => {
                      const isGroupOpen = openTipoId === group.tipo.id;
                      const items = activeTab === "combos" ? group.combos : group.services;
                      const selectedCountInGroup = items.filter((item) => isItemSelected(item)).length;
                      const panelId = `tipo-panel-${group.tipo.id}`;
                      const headerId = `tipo-header-${group.tipo.id}`;

                      return (
                        <div
                          key={`group-${group.tipo.id}`}
                          className={`rounded-2xl border transition-all overflow-hidden ${
                            isGroupOpen
                              ? category === "el"
                                ? "border-[#b79b7d]/50 bg-white shadow-sm"
                                : "border-[#cea498]/60 bg-white shadow-sm"
                              : selectedCountInGroup > 0
                              ? category === "el"
                                ? "border-[#b79b7d]/40 bg-white"
                                : "border-[#cea498]/40 bg-white"
                              : "border-[#3a3939]/10 bg-white hover:border-[#3a3939]/20"
                          }`}
                        >
                          <button
                            type="button"
                            id={headerId}
                            aria-expanded={isGroupOpen}
                            aria-controls={panelId}
                            onClick={() =>
                              setOpenTipoId((prev) => (prev === group.tipo.id ? null : group.tipo.id))
                            }
                            className="w-full flex items-center justify-between p-4 sm:p-5 text-left transition-colors hover:bg-[#3a3939]/[0.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498]"
                          >
                            <div className="flex items-center gap-2.5 flex-wrap">
                              <span
                                className={`font-serif-display text-base sm:text-lg font-semibold transition-colors ${
                                  isGroupOpen
                                    ? category === "el"
                                      ? "text-[#b79b7d]"
                                      : "text-[#593d36]"
                                    : "text-[#3a3939]"
                                }`}
                              >
                                {group.tipo.nombre}
                              </span>
                              <span className="text-[10px] sm:text-xs label text-[#8c8078] px-2.5 py-0.5 rounded-full bg-[#3a3939]/5">
                                {items.length}{" "}
                                {items.length === 1
                                  ? activeTab === "combos"
                                    ? "combo"
                                    : "servicio"
                                  : activeTab === "combos"
                                  ? "combos"
                                  : "servicios"}
                              </span>
                              {selectedCountInGroup > 0 && (
                                <span
                                  className={`text-[10px] font-bold label px-2.5 py-0.5 rounded-full ${
                                    category === "el"
                                      ? "bg-[#b79b7d]/20 text-[#b79b7d]"
                                      : "bg-[#cea498]/30 text-[#593d36]"
                                  }`}
                                >
                                  ✓ {selectedCountInGroup}{" "}
                                  {selectedCountInGroup === 1 ? "seleccionado" : "seleccionados"}
                                </span>
                              )}
                            </div>
                            <ChevronDown
                              className={`h-4 w-4 sm:h-5 sm:w-5 shrink-0 text-[#8c8078] transition-transform duration-200 motion-reduce:transition-none ${
                                isGroupOpen ? "rotate-180 text-[#3a3939]" : ""
                              }`}
                            />
                          </button>

                          {isGroupOpen && (
                            <div
                              id={panelId}
                              role="region"
                              aria-labelledby={headerId}
                              className="border-t border-[#3a3939]/5 p-3 sm:p-4 bg-[#faf9f6]/40"
                            >
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {activeTab === "combos"
                                  ? group.combos.map((combo) => {
                                      const isSelected = isItemSelected(combo);
                                      const componentNames = (combo.servicios_ids || [])
                                        .map((id) => {
                                          const s = services.find((srv) => srv.id === id);
                                          return s ? s.nombre : null;
                                        })
                                        .filter(Boolean);

                                      return (
                                        <button
                                          key={`combo-${combo.id}`}
                                          type="button"
                                          aria-pressed={isSelected}
                                          onClick={() => handleToggleItem(combo)}
                                          className={`flex flex-col text-left p-4 rounded-2xl border transition-all group relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498] ${
                                            isSelected
                                              ? category === "el"
                                                ? "border-[#b79b7d] bg-[#b79b7d]/15 shadow-sm ring-1 ring-[#b79b7d]"
                                                : "border-[#593d36] bg-[#cea498]/25 shadow-sm ring-1 ring-[#593d36]"
                                              : "border-[#3a3939]/10 bg-white hover:border-[#cea498] hover:shadow-sm"
                                          }`}
                                        >
                                          <div className="flex w-full items-start justify-between gap-3">
                                            <div className="flex flex-col gap-1">
                                              <span
                                                className={`font-serif-display text-base font-semibold text-[#3a3939] transition-colors ${
                                                  category === "el"
                                                    ? "group-hover:text-[#b79b7d]"
                                                    : "group-hover:text-[#cea498]"
                                                } ${
                                                  isSelected
                                                    ? category === "el"
                                                      ? "text-[#b79b7d] font-bold"
                                                      : "text-[#593d36] font-bold"
                                                    : ""
                                                }`}
                                              >
                                                {combo.nombre}
                                              </span>
                                              <span className="w-fit px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-[#593d36] text-[#faf9f6]">
                                                Combo · Experiencia
                                              </span>
                                            </div>
                                            <span
                                              className={`font-mono text-sm font-bold whitespace-nowrap ${
                                                category === "el" ? "text-[#b79b7d]" : "text-[#593d36]"
                                              }`}
                                            >
                                              ${Number(combo.precio).toLocaleString("es-CO")}
                                            </span>
                                          </div>

                                          {combo.descripcion && (
                                            <p className="mt-2 text-xs text-[#8c8078] line-clamp-2">
                                              {combo.descripcion}
                                            </p>
                                          )}

                                          {componentNames.length > 0 && (
                                            <div className="mt-2.5 text-xs text-[#6b615c] bg-[#3a3939]/5 p-2 rounded-xl">
                                              <span className="font-semibold text-[#3a3939]">Incluye: </span>
                                              {componentNames.join(" + ")}
                                            </div>
                                          )}

                                          <div className="mt-3 flex items-center justify-between w-full pt-1.5 border-t border-[#3a3939]/5">
                                            <span className="text-[10px] tracking-wider label text-[#6b615c] flex items-center gap-1">
                                              <Clock className="h-3 w-3" /> {combo.duracion_minutos} min
                                            </span>
                                            {isSelected && (
                                              <span
                                                className={`text-[10px] font-bold label ${
                                                  category === "el" ? "text-[#b79b7d]" : "text-[#593d36]"
                                                }`}
                                              >
                                                ✓ Seleccionado
                                              </span>
                                            )}
                                          </div>
                                        </button>
                                      );
                                    })
                                  : group.services.map((service) => {
                                      const isSelected = isItemSelected(service);
                                      return (
                                        <button
                                          key={`srv-${service.id}`}
                                          type="button"
                                          aria-pressed={isSelected}
                                          onClick={() => handleToggleItem(service)}
                                          className={`flex flex-col text-left p-4 rounded-2xl border transition-all group relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498] ${
                                            isSelected
                                              ? service.categoria === "el"
                                                ? "border-[#b79b7d] bg-[#b79b7d]/10 shadow-sm ring-1 ring-[#b79b7d]"
                                                : "border-[#593d36] bg-[#cea498]/20 shadow-sm ring-1 ring-[#593d36]"
                                              : "border-[#3a3939]/10 bg-white hover:border-[#cea498] hover:shadow-sm"
                                          }`}
                                        >
                                          <div className="flex w-full items-start justify-between gap-3">
                                            <span
                                              className={`font-serif-display text-base font-semibold text-[#3a3939] transition-colors ${
                                                service.categoria === "el"
                                                  ? "group-hover:text-[#b79b7d]"
                                                  : "group-hover:text-[#cea498]"
                                              } ${
                                                isSelected
                                                  ? service.categoria === "el"
                                                    ? "text-[#b79b7d] font-bold"
                                                    : "text-[#593d36] font-bold"
                                                  : ""
                                              }`}
                                            >
                                              {service.nombre}
                                            </span>
                                            <span
                                              className={`font-mono text-sm font-bold whitespace-nowrap ${
                                                service.categoria === "el" ? "text-[#b79b7d]" : "text-[#593d36]"
                                              }`}
                                            >
                                              ${Number(service.precio).toLocaleString("es-CO")}
                                            </span>
                                          </div>

                                          {service.descripcion && (
                                            <p className="mt-1.5 text-xs text-[#8c8078] line-clamp-2">
                                              {service.descripcion}
                                            </p>
                                          )}

                                          <div className="mt-3 flex items-center justify-between w-full pt-1.5 border-t border-[#3a3939]/5">
                                            <span className="text-[10px] tracking-wider label text-[#6b615c] flex items-center gap-1">
                                              <Clock className="h-3 w-3" /> {service.duracion_minutos} min
                                            </span>
                                            {isSelected && (
                                              <span
                                                className={`text-[10px] font-bold label ${
                                                  service.categoria === "el" ? "text-[#b79b7d]" : "text-[#593d36]"
                                                }`}
                                              >
                                                ✓ Seleccionado
                                              </span>
                                            )}
                                          </div>
                                        </button>
                                      );
                                    })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* PASO 2: Selección de Profesional */}
            {step === 2 && (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-1.5 text-xs label text-[#6b615c] hover:text-[#3a3939] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498] rounded-full px-2 py-1"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Volver a Experiencias
                </button>

                <div>
                  <h4 className="font-serif-display text-lg sm:text-xl text-[#3a3939]">Elige tu Especialista</h4>
                  <p className="text-xs text-[#8c8078] mt-0.5">
                    Selecciona al profesional que guiará tu ritual de bienestar
                  </p>
                </div>

                {loading ? (
                  <div className="flex justify-center py-16">
                    <Loader2
                      className={`h-8 w-8 animate-spin ${
                        category === "el" ? "text-[#b79b7d]" : "text-[#cea498]"
                      }`}
                    />
                  </div>
                ) : staffList.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[#3a3939]/20 p-8 text-center my-6">
                    <p className="font-cormorant text-lg italic text-[#6b615c]">
                      No hay profesionales activos que presten este servicio actualmente.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    {staffList.map((staff) => (
                      <button
                        key={staff.id}
                        type="button"
                        onClick={() => handleStaffSelect(staff)}
                        className={`flex items-center gap-4 p-4 rounded-2xl border border-[#3a3939]/10 bg-white transition-all text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498] ${
                          category === "el" ? "hover:border-[#b79b7d] hover:shadow-sm" : "hover:border-[#cea498] hover:shadow-sm"
                        }`}
                      >
                        <div
                          className={`flex h-11 w-11 items-center justify-center rounded-full font-serif-display text-base font-bold shrink-0 ${
                            category === "el" ? "bg-[#b79b7d]/20 text-[#b79b7d]" : "bg-[#cea498]/20 text-[#593d36]"
                          }`}
                        >
                          {staff.nombre[0]}
                        </div>
                        <div>
                          <p className="font-serif-display text-base font-semibold text-[#3a3939] group-hover:underline">
                            {staff.nombre}
                          </p>
                          <p className="text-xs text-[#8c8078]">Especialista de Bienestar</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* PASO 3: Selección de Fecha y Hora */}
            {step === 3 && (
              <div className="space-y-5">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="inline-flex items-center gap-1.5 text-xs label text-[#6b615c] hover:text-[#3a3939] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498] rounded-full px-2 py-1"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Volver a Especialista
                </button>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#3a3939]/10 pb-4">
                  <div>
                    <h4 className="font-serif-display text-lg sm:text-xl text-[#3a3939]">Fecha y Horario</h4>
                    <p className="text-xs text-[#8c8078] mt-0.5">
                      Con {selectedStaff?.nombre} · Duración: {totalDuration} min
                    </p>
                  </div>
                  <div className="w-full sm:w-auto">
                    <label htmlFor="booking-date-input" className="sr-only">
                      Seleccionar fecha de cita
                    </label>
                    <input
                      id="booking-date-input"
                      aria-label="Seleccionar fecha de cita"
                      type="date"
                      min={todayStr}
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full sm:w-auto rounded-xl border border-[#3a3939]/20 bg-white px-4 py-2.5 text-sm font-medium text-[#3a3939] focus:outline-none focus:ring-2 focus:ring-[#cea498]"
                    />
                  </div>
                </div>

                {slotsLoading ? (
                  <div className="flex justify-center py-16">
                    <Loader2
                      className={`h-8 w-8 animate-spin ${
                        category === "el" ? "text-[#b79b7d]" : "text-[#cea498]"
                      }`}
                    />
                  </div>
                ) : !selectedDate ? (
                  <div className="rounded-2xl border border-dashed border-[#3a3939]/20 p-8 text-center my-6">
                    <Calendar className="h-8 w-8 mx-auto text-[#8c8078] mb-2 opacity-50" />
                    <p className="font-cormorant text-lg italic text-[#6b615c]">
                      Selecciona una fecha en el calendario para consultar los horarios disponibles.
                    </p>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[#3a3939]/20 p-8 text-center my-6">
                    <Clock className="h-8 w-8 mx-auto text-[#8c8078] mb-2 opacity-50" />
                    <p className="font-cormorant text-lg italic text-[#6b615c]">
                      No hay turnos disponibles para esta fecha con el especialista seleccionado. Por favor intenta con otro día.
                    </p>
                  </div>
                ) : (
                  <div>
                    <h5 className="text-xs label text-[#8c8078] uppercase tracking-wider mb-3">
                      Turnos disponibles:
                    </h5>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {availableSlots.map((slot) => {
                        const isChosen = selectedTime === slot;
                        return (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setSelectedTime(slot)}
                            className={`rounded-xl py-3 px-2 text-center text-xs font-mono font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498] ${
                              isChosen
                                ? category === "el"
                                ? "bg-[#b79b7d] text-[#1a1a1a] shadow-sm font-bold"
                                : "bg-[#593d36] text-[#faf9f6] shadow-sm font-bold"
                                : "bg-white border border-[#3a3939]/10 text-[#3a3939] hover:border-[#cea498]"
                            }`}
                          >
                            {slot}
                          </button>
                        );
                      })}
                    </div>

                    {totalDuration > 240 && (
                      <div className="mt-4 rounded-2xl border border-amber-200/80 bg-amber-50/80 p-3.5 text-xs text-amber-900 flex items-start gap-2.5">
                        <span className="text-base leading-none">⚠️</span>
                        <span>
                          Tu ritual seleccionado supera las 4 horas de duración total. Recomendamos dividirlo en diferentes sesiones para mejorar tu experiencia.
                        </span>
                      </div>
                    )}

                    {selectedTime && (
                      <div className="mt-6 p-4 rounded-2xl bg-white border border-[#3a3939]/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="text-xs text-[#6b615c]">
                          <span>Horario programado: </span>
                          <strong className="text-[#3a3939]">
                            {selectedTime} a {getEndTime(selectedTime, totalDuration)}
                          </strong>
                        </div>
                        <button
                          type="button"
                          onClick={handleDateTimeSubmit}
                          className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 text-xs font-bold uppercase tracking-wider transition-all shadow-sm ${
                            category === "el"
                              ? "bg-[#b79b7d] text-[#1a1a1a] hover:bg-[#8c7a6b] hover:text-white"
                              : "bg-[#cea498] text-[#1a1a1a] hover:bg-[#593d36] hover:text-[#faf9f6]"
                          }`}
                        >
                          Continuar a Datos <ArrowRight className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* PASO 4: Formulario de Datos del Cliente */}
            {step === 4 && (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="inline-flex items-center gap-1.5 text-xs label text-[#6b615c] hover:text-[#3a3939] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498] rounded-full px-2 py-1"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Volver a Horarios
                </button>

                <div>
                  <h4 className="font-serif-display text-lg sm:text-xl text-[#3a3939]">Tus Datos Personales</h4>
                  <p className="text-xs text-[#8c8078] mt-0.5">
                    Comunícanos tus datos para confirmar tu reserva y enviarte recordatorios
                  </p>
                </div>

                <form onSubmit={handleFinalSubmit} className="space-y-4 pt-2">
                  {/* 1. Teléfono Primero */}
                  <div>
                    <label className="block text-xs label text-[#6b615c] mb-1.5">
                      Teléfono / WhatsApp *
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        name="telefono_cliente"
                        required
                        value={formData.telefono_cliente}
                        onChange={handlePhoneChange}
                        placeholder="Ej. 300 123 4567 o +57 300 123 4567"
                        className="w-full rounded-xl border border-[#3a3939]/20 bg-white p-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#cea498]"
                        disabled={loading}
                      />
                      {phoneCheckStatus === "checking" && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#cea498]">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      )}
                      {phoneCheckStatus === "exists_active" && isConfirmedSelf && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600">
                          <Check className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                    {phoneErrorMessage && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5 inline shrink-0" />
                        {phoneErrorMessage}
                      </p>
                    )}
                    {phoneCheckStatus === "idle" &&
                      formData.telefono_cliente.trim().replace(/[^\d]/g, "").length >= 7 && (
                        <div className="mt-1.5 flex justify-end">
                          <button
                            type="button"
                            onClick={() => checkPhone(formData.telefono_cliente)}
                            className="text-xs font-semibold px-3 py-1 rounded-full bg-[#cea498]/20 text-[#3a3939] hover:bg-[#cea498]/30 transition-colors"
                          >
                            Verificar teléfono
                          </button>
                        </div>
                      )}
                  </div>

                  {/* 2. Tarjeta cuando existe y está activo */}
                  {phoneCheckStatus === "exists_active" && (
                    <div className="rounded-2xl border border-[#cea498]/40 bg-[#fbf9f6] p-4 text-xs space-y-3 shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-[#cea498]/20 text-[#3a3939] shrink-0 mt-0.5">
                          <User className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] uppercase tracking-wider text-[#8c8078] font-bold">
                            Cuenta DUO encontrada
                          </p>
                          <p className="text-sm font-semibold text-[#3a3939] mt-0.5">
                            Titular registrado:{" "}
                            <span className="text-[#593d36] font-serif-display text-base tracking-wide">
                              {phoneCheckResult?.masked_name}
                            </span>
                          </p>
                        </div>
                      </div>

                      {!isConfirmedSelf && !isRejectedSelf && (
                        <div className="pt-2 border-t border-[#cea498]/20">
                          <p className="text-xs text-[#6b615c] mb-3">
                            ¿Confirmas que eres tú para vincular tu reserva a esta ficha?
                          </p>
                          <div className="flex flex-wrap gap-2.5">
                            <button
                              type="button"
                              onClick={() => {
                                setIsConfirmedSelf(true);
                                setIsRejectedSelf(false);
                              }}
                              className={`flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2 text-white text-xs font-bold shadow-sm transition-all ${
                                category === "el"
                                  ? "bg-[#1a1a1a] hover:bg-[#b79b7d] hover:text-[#1a1a1a]"
                                  : "bg-[#3a3939] hover:bg-[#cea498] hover:text-[#1a1a1a]"
                              }`}
                            >
                              <Check className="h-3.5 w-3.5" /> Sí, soy yo
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsRejectedSelf(true);
                                setIsConfirmedSelf(false);
                              }}
                              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 border border-[#3a3939]/20 text-[#6b615c] hover:bg-black/5 text-xs font-medium transition-colors"
                            >
                              No soy yo
                            </button>
                          </div>
                        </div>
                      )}

                      {isConfirmedSelf && (
                        <div className="pt-2 border-t border-[#cea498]/20 flex items-center justify-between text-[11px] text-[#6b615c]">
                          <span className="text-emerald-700 font-medium flex items-center gap-1">
                            <CheckCircle className="h-3.5 w-3.5 inline text-emerald-600" /> Identidad confirmada
                          </span>
                          <button
                            type="button"
                            onClick={() => setIsConfirmedSelf(false)}
                            className="text-[#8c8078] hover:text-[#3a3939] underline transition-colors"
                          >
                            Cambiar
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 3. Tarjeta de rechazo de identidad ("No soy yo") */}
                  {isRejectedSelf && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 text-xs space-y-3">
                      <div className="flex items-start gap-2.5">
                        <AlertCircle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                        <p className="text-amber-900 leading-relaxed">
                          Este número telefónico ya está asociado a otra cuenta en DUO Experience. Para proteger la seguridad y el historial de nuestros clientes, por favor contáctanos directamente para verificar tu información.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 pt-1">
                        <a
                          href="https://wa.me/573045646235?text=Hola%20DUO%20Experience,%20mi%20número%20aparece%20registrado%20con%20otro%20nombre%20y%20necesito%20asistencia%20con%20mi%20reserva"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold text-xs shadow-sm transition-all"
                        >
                          <MessageCircle className="h-4 w-4" /> Contactar por WhatsApp
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            setIsRejectedSelf(false);
                            setPhoneCheckStatus("idle");
                            setFormData((prev) => ({ ...prev, telefono_cliente: "" }));
                          }}
                          className="text-xs text-[#6b615c] hover:text-[#3a3939] underline"
                        >
                          Ingresar otro número
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 4. Tarjeta de cuenta inactiva */}
                  {phoneCheckStatus === "exists_inactive" && (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50/70 p-4 text-xs space-y-3">
                      <div className="flex items-start gap-2.5">
                        <AlertCircle className="h-4 w-4 text-rose-700 shrink-0 mt-0.5" />
                        <p className="text-rose-900 leading-relaxed">
                          Esta cuenta se encuentra inactiva. Comunícate con DUO Experience para reactivarla.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 pt-1">
                        <a
                          href="https://wa.me/573045646235?text=Hola%20DUO%20Experience,%20deseo%20reactivar%20mi%20cuenta%20para%20realizar%20una%20reserva"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold text-xs shadow-sm transition-all"
                        >
                          <MessageCircle className="h-4 w-4" /> Contactar por WhatsApp
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            setPhoneCheckStatus("idle");
                            setFormData((prev) => ({ ...prev, telefono_cliente: "" }));
                          }}
                          className="text-xs text-[#6b615c] hover:text-[#3a3939] underline"
                        >
                          Ingresar otro número
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 5. Campos condicionales para nuevo cliente */}
                  {phoneCheckStatus === "not_found" && (
                    <div className="space-y-4 pt-1">
                      <div className="p-2.5 rounded-xl bg-[#cea498]/10 border border-[#cea498]/20 text-[11px] text-[#6b615c]">
                        Primera vez en DUO Experience: completa tu nombre y correo para crear tu ficha.
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs label text-[#6b615c] mb-1.5">Nombre Completo *</label>
                          <input
                            type="text"
                            name="nombre_cliente"
                            required
                            value={formData.nombre_cliente}
                            onChange={handleInputChange}
                            placeholder="Ej. Juan Pérez"
                            className="w-full rounded-xl border border-[#3a3939]/20 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#cea498]"
                          />
                        </div>
                        <div>
                          <label className="block text-xs label text-[#6b615c] mb-1.5">Correo Electrónico *</label>
                          <input
                            type="email"
                            name="email_cliente"
                            required
                            value={formData.email_cliente}
                            onChange={handleInputChange}
                            placeholder="correo@ejemplo.com"
                            className="w-full rounded-xl border border-[#3a3939]/20 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#cea498]"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs label text-[#6b615c] mb-1.5">Fecha de cumpleaños (Opcional)</label>
                        <input
                          type="date"
                          name="fecha_nacimiento"
                          value={formData.fecha_nacimiento}
                          onChange={handleInputChange}
                          className="w-full rounded-xl border border-[#3a3939]/20 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#cea498]"
                        />
                        <p className="mt-1 text-[11px] text-[#8c8078]">
                          Queremos conocer tu fecha de cumpleaños para darte una sorpresa especial.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* 6. Observaciones / Preferencias (Opcional) */}
                  {(phoneCheckStatus === "not_found" || (phoneCheckStatus === "exists_active" && isConfirmedSelf)) && (
                    <div>
                      <label className="block text-xs label text-[#6b615c] mb-1.5">
                        Observaciones / Preferencias (Opcional)
                      </label>
                      <textarea
                        name="notas"
                        value={formData.notas}
                        onChange={handleInputChange}
                        placeholder="Mencione alergias, masajes más suaves, etc."
                        rows={2}
                        className="w-full rounded-xl border border-[#3a3939]/20 bg-white p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#cea498]"
                      />
                    </div>
                  )}

                  {/* Resumen previo a confirmación */}
                  <div className="p-4 rounded-2xl bg-white border border-[#3a3939]/10 text-xs space-y-1.5">
                    <p className="font-semibold text-[#3a3939]">Resumen de la cita:</p>
                    <p className="text-[#6b615c]">
                      • Especialista: <strong>{selectedStaff?.nombre}</strong>
                    </p>
                    <p className="text-[#6b615c]">
                      • Fecha y hora: <strong>{selectedDate} a las {selectedTime}</strong>
                    </p>
                    <p className="text-[#6b615c]">
                      • Total a pagar:{" "}
                      <strong className={category === "el" ? "text-[#b79b7d]" : "text-[#593d36]"}>
                        ${totalValue.toLocaleString("es-CO")} COP
                      </strong>{" "}
                      ({totalDuration} min)
                    </p>
                  </div>

                  {/* Botón de Confirmación */}
                  {(phoneCheckStatus === "not_found" || (phoneCheckStatus === "exists_active" && isConfirmedSelf)) && (
                    <button
                      type="submit"
                      disabled={loading}
                      className={`w-full flex items-center justify-center gap-2 rounded-full py-4 text-white transition-all label text-xs uppercase tracking-wider font-bold shadow-md disabled:opacity-50 ${
                        category === "el"
                          ? "bg-[#1a1a1a] hover:bg-[#b79b7d] hover:text-[#1a1a1a]"
                          : "bg-[#3a3939] hover:bg-[#cea498] hover:text-[#1a1a1a]"
                      }`}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Agendando ritual...
                        </>
                      ) : (
                        "Confirmar Reserva de Experiencia"
                      )}
                    </button>
                  )}
                </form>
              </div>
            )}

            {/* PASO 5: Confirmación de Éxito */}
            {step === 5 && success && (
              <div className="flex flex-col items-center text-center py-6 sm:py-8 space-y-5 max-w-lg mx-auto">
                <CheckCircle
                  className={`h-16 w-16 ${category === "el" ? "text-[#b79b7d]" : "text-[#cea498]"}`}
                />
                <div>
                  <h4 className="font-serif-display text-2xl sm:text-3xl text-[#3a3939] mb-1">
                    ¡Reserva Registrada!
                  </h4>
                  <p className="text-xs sm:text-sm text-[#6b615c]">
                    Tu cita ha sido guardada en la base de datos de DUO Experience. Te esperamos para vivir una experiencia inolvidable.
                  </p>
                </div>

                <div className="w-full rounded-2xl bg-white border border-[#3a3939]/10 p-5 text-left text-sm grid gap-3 shadow-sm">
                  <div className="flex items-start gap-3 text-[#3a3939]">
                    <ShoppingBag
                      className={`h-4 w-4 mt-1 ${category === "el" ? "text-[#b79b7d]" : "text-[#cea498]"}`}
                    />
                    <div>
                      <p className="text-[10px] label text-[#6b615c] uppercase tracking-wider">
                        Rituales Reservados
                      </p>
                      <div className="font-semibold space-y-1">
                        {selectedServices.map((s) => (
                          <div key={s.es_combo ? `combo-${s.id}` : `srv-${s.id}`}>
                            • {s.nombre} ({s.duracion_minutos} min)
                            {s.es_combo ? " [Combo · Experiencia]" : ""}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-[#3a3939]">
                    <User className={`h-4 w-4 ${category === "el" ? "text-[#b79b7d]" : "text-[#cea498]"}`} />
                    <div>
                      <p className="text-[10px] label text-[#6b615c] uppercase tracking-wider">
                        Especialista de Bienestar
                      </p>
                      <p className="font-semibold">{selectedStaff?.nombre}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-[#3a3939]">
                    <Calendar
                      className={`h-4 w-4 ${category === "el" ? "text-[#b79b7d]" : "text-[#cea498]"}`}
                    />
                    <div>
                      <p className="text-[10px] label text-[#6b615c] uppercase tracking-wider">
                        Fecha y Hora Programada
                      </p>
                      <p className="font-semibold">
                        {new Date(`${selectedDate}T${selectedTime}`).toLocaleString("es-CO", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </p>
                    </div>
                  </div>

                  {success.es_cortesia_lealtad && (
                    <div
                      className={`mt-2 rounded-xl px-3 py-2 text-xs font-semibold border ${
                        category === "el"
                          ? "bg-[#b79b7d]/20 border-[#b79b7d] text-[#b79b7d]"
                          : "bg-[#cea498]/20 border-[#cea498] text-[#593d36]"
                      }`}
                    >
                      🎁 ¡Felicidades! Esta cita califica como cortesía gratis por lealtad.
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className={`rounded-full px-10 py-3.5 text-white transition-all label text-xs uppercase tracking-wider font-bold shadow-md ${
                    category === "el"
                      ? "bg-[#b79b7d] hover:bg-[#8c7a6b] hover:text-[#1a1a1a]"
                      : "bg-[#3a3939] hover:bg-[#cea498] hover:text-[#1a1a1a]"
                  }`}
                >
                  Terminar Agendamiento
                </button>
              </div>
            )}
          </div>

          {/* Barra inferior fija de resumen y continuación (Visible en Paso 1 si hay elementos seleccionados) */}
          {step === 1 && selectedServices.length > 0 && (
            <div
              className="flex-shrink-0 border-t border-[#3a3939]/10 bg-white p-3.5 sm:px-6 sm:py-4 shadow-[0_-4px_16px_rgba(0,0,0,0.05)]"
              style={{ paddingBottom: "max(0.875rem, env(safe-area-inset-bottom, 0px))" }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="font-serif-display text-sm sm:text-base font-bold text-[#3a3939]">
                      {selectedServices.length === 1 && selectedServices[0].es_combo
                        ? `Combo: ${selectedServices[0].nombre}`
                        : `${selectedServices.length} ${
                            selectedServices.length === 1 ? "ritual seleccionado" : "rituales seleccionados"
                          }`}
                    </span>
                    <button
                      type="button"
                      onClick={handleClearSelections}
                      className="text-[11px] underline text-[#8c8078] hover:text-[#593d36]"
                    >
                      Quitar
                    </button>
                  </div>
                  <span className="text-xs text-[#6b615c]">
                    Duración aprox: <strong>{totalDuration} min</strong> · Total:{" "}
                    <strong className={category === "el" ? "text-[#b79b7d]" : "text-[#593d36]"}>
                      ${totalValue.toLocaleString("es-CO")} COP
                    </strong>
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-xs font-bold uppercase tracking-wider transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cea498] shadow-sm ${
                    category === "el"
                      ? "bg-[#b79b7d] text-[#1a1a1a] hover:bg-[#8c7a6b] hover:text-white"
                      : "bg-[#cea498] text-[#1a1a1a] hover:bg-[#593d36] hover:text-[#faf9f6]"
                  }`}
                >
                  Continuar con Especialista <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BookingModal;
